import React, { type ReactNode, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { ExpandMore as ExpandIcon } from '@mui/icons-material';
import { borderRadius, layoutGap } from '../../../theme/theme';
import ActionHelpTooltip from '../common/ActionHelpTooltip';
import type { HelpValue } from '../common/helpModel';
import {
  getWorkflowWorkspacePalette,
  getWorkspaceIconButtonSx,
  WorkspaceNavRow,
  WorkspacePane,
  type WorkspaceTone,
} from './WorkflowWorkspacePrimitives';

export interface WorkspaceFilterRailProps {
  children: ReactNode;
  dataTestId?: string;
  sx?: SxProps<Theme>;
}

export function WorkspaceFilterRail({ children, dataTestId, sx }: WorkspaceFilterRailProps) {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);

  return (
    <WorkspacePane
      dataTestId={dataTestId}
      sx={[
        {
          borderRight: `1px solid ${palette.paneBorder}`,
          backgroundColor: palette.paneBackground,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </WorkspacePane>
  );
}

export interface WorkspaceFilterSectionProps {
  title: ReactNode;
  description?: ReactNode;
  descriptionBold?: boolean;
  children: ReactNode;
  action?: ReactNode;
  highlighted?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  dataTestId?: string;
}

export function WorkspaceFilterSection({
  title,
  description,
  descriptionBold = false,
  children,
  action,
  highlighted = false,
  collapsible = false,
  defaultExpanded = true,
  expanded: expandedProp,
  onToggle,
  dataTestId,
}: WorkspaceFilterSectionProps) {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);
  const [expandedState, setExpandedState] = useState(defaultExpanded);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : expandedState;

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
      return;
    }

    setExpandedState((previous) => !previous);
  };

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        px: 0.85,
        py: 0.82,
        borderRadius: `${borderRadius.sm}px`,
        border: `1px solid ${highlighted ? palette.selectedBorder : palette.sectionBorder}`,
        backgroundColor: highlighted ? palette.selectedBackground : palette.sectionBackground,
      }}
    >
      <Stack direction="row" spacing={layoutGap.dense} alignItems="center" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 700,
              fontSize: '0.78rem',
              letterSpacing: 0,
              textTransform: 'none',
              color: palette.headingText,
            }}
          >
            {title}
          </Typography>
          {description ? (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: descriptionBold ? 700 : undefined,
                fontSize: '0.78rem',
                color: descriptionBold ? palette.headingText : palette.mutedText,
                lineHeight: 1.45,
              }}
            >
              {description}
            </Typography>
          ) : null}
        </Box>
        <Stack direction="row" spacing={layoutGap.dense} alignItems="center">
          {action}
          {collapsible ? (
            <IconButton
              size="small"
              onClick={handleToggle}
              aria-label={expanded ? 'Collapse section' : 'Expand section'}
              aria-expanded={expanded}
              sx={getWorkspaceIconButtonSx(theme, { size: 26 })}
            >
              <ExpandIcon
                sx={{
                  fontSize: 16,
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </IconButton>
          ) : null}
        </Stack>
      </Stack>
      {collapsible ? (
        <Collapse in={expanded}>
          <Stack spacing={layoutGap.snug} sx={{ mt: layoutGap.snug }}>
            {children}
          </Stack>
        </Collapse>
      ) : (
        <Stack spacing={layoutGap.snug} sx={{ mt: layoutGap.snug }}>
          {children}
        </Stack>
      )}
    </Box>
  );
}

export interface WorkspaceFilterItemProps {
  icon: ReactNode;
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
  tone?: WorkspaceTone | 'critical';
  badge?: ReactNode;
  help?: HelpValue;
  dataTestId?: string;
}

export function WorkspaceFilterItem({
  icon,
  label,
  count,
  selected,
  onClick,
  tone = 'neutral',
  badge,
  help,
  dataTestId,
}: WorkspaceFilterItemProps) {
  const theme = useTheme();
  const rowTone: WorkspaceTone = tone === 'critical' ? 'error' : tone;

  const row = (
    <WorkspaceNavRow
      dataTestId={dataTestId}
      onClick={onClick}
      selected={selected}
      tone={rowTone}
      leading={icon}
      title={label}
      sx={{
        px: 0.9,
        py: 0.82,
        borderColor: selected ? undefined : 'transparent',
      }}
      trailing={
        badge || (
          <Typography
            variant="caption"
            sx={{
              minWidth: 22,
              textAlign: 'right',
              fontWeight: selected ? 800 : 650,
              fontSize: '0.74rem',
              color: selected ? 'inherit' : 'text.secondary',
            }}
          >
            {count}
          </Typography>
        )
      }
    />
  );

  if (!help) {
    return row;
  }

  return (
    <ActionHelpTooltip help={help} placement="right" fullWidth>
      {row}
    </ActionHelpTooltip>
  );
}
